import Vue from 'vue'
import Vuex from 'vuex'
import moment from 'moment'
import axios from 'axios'
import camelcaseKeys from 'camelcase-keys'
import TreeModel from './tree-model.es6'
import LogsCache from './observer/history/logs-cache'
import checklistStore from './observer/checklists/checklist-store.es6'
import lazyItemsStore from './store-items.es6'
import mapStore from './observer/map/map-store.es6'
import filtersStore from '@/observer/shared/filters-store.es6'
import i18n from './i18n.js'

Vue.use(Vuex)

// Converts all keys in the entire object (even deep-nested) from underscore to camelCase
export function underscoresToCamels (item) {
  return camelcaseKeys(item, { deep: true })
}

// Shared mutations
function vuexSetCurrentUser (state, user) {
  Vue.set(state.auth, 'currentUser', user)
  if (user) {
    // Sets also the currentOwnerId to user default
    Vue.set(state.auth, 'currentOwnerId', user.ownerId)
  }
}

function vuexSetErrors (state, modelName, errors) {
  const convertedErrors = state.camelCaseBE ? errors : underscoresToCamels(errors)
  Vue.set(state.itemErrors, modelName, convertedErrors)
}

function vuexSetError (state, errorType, val) {
  console.log('vuexSetError', errorType, val)
  Vue.set(state.errors, errorType, val)
}

let initItems = {
  assets: [],
  issues: [],
  interventions: [],
  'asset-categories': [],
  'asset-attributes': [],
  'asset-types': [],
  'planned-issue-definitions': [],
  'issue-codes': [],
  products: [],
  users: [],
  teams: [],
  elements: [],
  'element-types': [],
  visuals: [],
  owners: [],
  projects: [],
  'pipe-tasks': [],
  files: []
}
let initItemsLoaded = {
  assets: false,
  issues: false,
  interventions: false,
  'asset-categories': false,
  'asset-attributes': false,
  'asset-types': false,
  'planned-issue-definitions': false,
  'issue-codes': false,
  products: false,
  users: false,
  teams: false,
  elements: false,
  'element-types': false,
  visuals: false,
  owners: false,
  projects: false,
  'pipe-tasks': false,
  files: false
}
let initItem = {
  assets: {},
  issues: {},
  interventions: {},
  'asset-categories': {},
  'asset-attributes': {},
  'asset-types': {},
  'planned-issue-definitions': {},
  'issue-codes': {},
  products: {},
  users: {},
  teams: {},
  elements: {},
  'element-types': {},
  visuals: {},
  owners: {},
  projects: {},
  'pipe-tasks': {},
  files: {},
  checklists: {}
}
let initItemErrors = Object.assign({}, initItem) // Same structure as item (re-using the template)
const initDestroyDialog = {
  active: false,
  title: '',
  body: '',
  resolve: null,
  reject: null
}

const initUnsavedDialog = {
  active: false,
  title: '',
  body: '',
  resolve: null,
  reject: null
}

const logsCache = new LogsCache()

let store = new Vuex.Store({
  state: {
    newVersionError: false,
    feVersion: process.env.PACKAGE_VERSION,
    camelCaseBE: true, // Indicates that BE is providing keys in camelCase (Node.js) format
    auth: {
      currentUser: {},
      currentOwnerId: undefined, // User-selected owner (for assets visibility), defaults to User.ownerId
      currentTeam: {}
    },
    userAgent: {
      isDesktop: undefined, // Bool
      isMobile: undefined // Bool
    },
    siteName: '',
    siteModules: [],
    site: {},
    issues: {
      limitDays: 14
    },
    gmap: {
      center: { lat: 50.08, lng: 14.50 },
      zoom: 13,
      style: 'osm' // osm | google
    },
    report: {
      date: moment().startOf('day'),
      filter: 'default'
    },
    lastPage: '',
    // FLAT PARAMS
    developerMode: false,
    useDemoDataInVisual: false,
    // ITEMS
    items: Object.assign({}, initItems),
    // For infinity loading (when true, items has all the data)
    // Is only set when using the addManyItems
    itemsLoaded: Object.assign({}, initItemsLoaded),
    staticItems: {},
    item: Object.assign({}, initItem),
    // Real-time data
    deviceDefinitions: {},
    deviceTypes: {},
    assetData: {},
    // Report
    reportItems: [],
    deviceStatisticsReportData: {},
    // Errors
    errors: {
      errorTexts: [],
      genericError: false,
      networkError: false,
      serverError: false,
      unauthorizedError: false
    },
    // Snackbars
    savedSnackbar: false,
    snackbar: false,
    archivedSnackbar: false,
    // Websocket connection status
    hostname: '',
    websocketStatus: 'offline', // offline/connecting/online
    shConnectionStatus: 'offline',
    shLog: [],
    // Others
    itemErrors: Object.assign({}, initItemErrors),
    destroyDialog: Object.assign({}, initDestroyDialog),
    unsavedDialog: Object.assign({}, initUnsavedDialog),
    files: {},
    mediaDevices: []
  },

  mutations: {
    setCurrentUser (state, user) {
      // Vue.set(state.auth, currentUser, user)
      // state.auth.currentUser = user
      vuexSetCurrentUser(state, user)
    },
    setCurrentOwnerId (state, val) {
      state.auth.currentOwnerId = val
    },
    setCurrentTeam (state, team) {
      // Vue.set(state.auth, currentTeam, team)
      state.auth.currentTeam = team
    },
    setUserAgent (state, val) {
      state.userAgent = val
    },
    setSiteName (state, siteName) {
      state.siteName = siteName
    },
    setSiteModules (state, siteModules) {
      state.siteModules = siteModules
    },
    setSite (state, site) {
      // Loads entire object of the site config
      state.site = site
    },
    toggleFlat (state, param) {
      // Toggles value of boolean flat param of the state (not deep-nested)
      state[param] = !state[param]
    },
    setFlat (state, { param, val }) {
      // Sets value of flat param of the state (not deep-nested)
      state[param] = val
    },
    setMapOptions (state, val) {
      Object.keys(val)
        .filter(key => val[key] === undefined)
        .forEach(key => delete val[key])

      Object.assign(state.gmap, val)
    },
    emptyAssetData (state) {
      state.assetData = {}
    },
    setError (state, { errorType, val }) {
      vuexSetError(state, errorType, val)
    },
    setReport (state, { param, val }) {
      Vue.set(state.report, param, val)
    },
    setLastPage (state, lastPage) {
      state.lastPage = lastPage
    },
    emptyItemsSpace (state) {
      console.log('[store][emptyItemsSpace]')
      state.items = Object.assign({}, initItems)
      state.itemsLoaded = Object.assign({}, initItemsLoaded)
      state.item = Object.assign({}, initItem)
      // Report items
      Vue.set(state, 'reportItems', [])
      Vue.set(state, 'deviceStatisticsReportData', {})
      // Errors (must be set individually as they are used in main-window - Vue reactivity)
      state.errors.errorTexts = []
      state.errors.genericError = false
      state.errors.networkError = false
      state.errors.serverError = false
      state.errors.unauthorizedError = false
      // Snackbars
      state.savedSnackbar = false
      state.snackbar = false
      state.archivedSnackbar = false
      // item errors
      state.itemErrors = Object.assign({}, initItemErrors)
    },
    setItems (state, { modelName, modelTree, items }) {
      console.log('setItems, modelName: ' + modelName)
      const convertedItems = state.camelCaseBE ? items : items.map(item => underscoresToCamels(item))
      if (Array.isArray(convertedItems)) {
        if (!modelTree) {
          // Tabular data
          // Save to store
          Vue.set(state.items, modelName, convertedItems)
        } else {
          // Tree data (converting flat to tree and saving)
          const tree = new TreeModel({ payloads: convertedItems })
          Vue.set(state.items, modelName, tree)
        }
      } else {
        console.log('ERR: items is not an array')
      }
    },
    addManyItems (state, { modelName, items, toBeginning = false }) {
      console.log('addManyItems, modelName: ' + modelName)
      const convertedItems = state.camelCaseBE ? items : items.map(item => underscoresToCamels(item))
      if (Array.isArray(convertedItems)) {
        if (toBeginning) {
          state.items[modelName] = [...convertedItems, ...state.items[modelName]]
        } else {
          state.items[modelName] = [...state.items[modelName], ...convertedItems]
        }
      } else {
        console.log('ERR: items is not an array')
      }
    },
    setStaticItems (state, { modelName, modelTree, items }) {
      console.log('setStaticItems, modelName: ' + modelName)
      const convertedItems = state.camelCaseBE ? items : items.map(item => underscoresToCamels(item))
      if (Array.isArray(convertedItems)) {
        if (!modelTree) {
          // Tabular data, save to store
          Vue.set(state.staticItems, modelName, convertedItems)
        } else {
          // Tree data, convert flat to tree and save
          const tree = new TreeModel({ payloads: convertedItems })
          Vue.set(state.staticItems, modelName, tree)
        }
      } else {
        console.log('ERR: items is not an array')
      }
    },
    setItem (state, { modelName, item }) {
      console.log('setItem, modelName: ' + modelName)
      const convertedItem = state.camelCaseBE ? item : underscoresToCamels(item)
      Vue.set(state.item, modelName, convertedItem)
    },
    modifyItem (state, { modelName, col, val }) {
      console.log('modifyItem, modelName: ' + modelName + ', col: ' + col + ', val: ' + val)
      Vue.set(state.item[modelName], col, val)
    },
    resetItemsLoaded (state, { model }) {
      console.log('[store][resetItemsLoaded]')
      state.itemsLoaded[model.name] = false
    },
    receiveFromWebSocket (state, { msg, vm }) {
      // Processes data received via websocket
      const { responseType, assetId, assetIds, method, variables, item } = JSON.parse(msg)
      // Handling resposnseType == Result or Params
      if (['Result', 'Params'].includes(responseType)) {
        // console.log(msg)
        // Parsing the websocket message

        if (/^alarm/.test(method) && !state.items.alarms)
        return

        // Alarms-related
        if (method === 'alarmUpdated') {
          console.log('[Alarm][updated]', item)
          const id = state.items.alarms
            .findIndex(element => element.id === item.id)
          Vue.set(state.items.alarms, id, item)
        } else if (method === 'alarmDeleted') {
          // console.log('[Alarm][deleted]', item)
          const id = state.items.alarms
            .findIndex(element => element.id === item.id)
          Vue.delete(state.items.alarms, id)
        } else if (method === 'alarmCreated') {
          // console.log('[Alarm][created]', item)
          state.items.alarms.push(item)
          // Variables-related
        } else if (method === 'disconnected') {
          // update all disconnected assets
          let { renStage } = vm.$options
          for (let assetId of assetIds || []) {
            Vue.set(state.assetData, assetId, null)
            if (renStage) {
              renStage.updateElement(assetId, {})
            }
          }
        } else if (method === 'update' && assetId) {
          // console.log(`[DATA] id: ${assetId}, variables: ${variables}`)
          // cant use second option - it may not create new object and watcher for assetData then wont fire
          const newVariables = Object.assign({}, state.assetData[assetId] || {}, { online: true }, variables)
          // const newVariables = Object.assign(state.assetData[assetId] || {}, variables)
          // const newVariables = {...state.assetData[assetId], ...variables}

          Vue.set(state.assetData, assetId, newVariables)
          if (vm.$options.renStage) {
            vm.$options.renStage.updateElement(assetId, newVariables, state.deviceTypes)
          }

        } else if (!assetId) {
          // Vue.set(state, 'shLog', [{assetId: null, method: 'missing asset', result: 'Asset not found'}, ...state.shLog])
        } else {
          Vue.set(state, 'shLog', [{assetId: assetId, method: method, result: variables}, ...state.shLog])
        }
      } else if (responseType === 'SHStatus') {
        const shStatus = !!variables
        state.shConnectionStatus = shStatus
      } else if (responseType === 'Error') {
        Vue.set(state, 'shLog', [{method: method, result: variables['2']}, ...state.shLog])
      }
    },
    updateAssetData (state, { id, variables }) {
      const data = state.assetData[id] || {}
      Vue.set(state.assetData, id, Object.assign(data, variables))
    },
    setReportItems (state, { items }) {
      Vue.set(state, 'reportItems', items)
    },
    setDeviceStatisticsReportData (state, { assetId, data }) {
      Vue.set(state.deviceStatisticsReportData, assetId, data)
    },
    clearDeviceStatisticsReportData (state) {
      Vue.set(state, 'deviceStatisticsReportData', {})
    },
    clearLogs (state) {
      console.log('clearLogs')
      Vue.set(state, 'assetLogs', {})
    },
    setLogs (state, { assetId, data }) {
      console.log('setLogs', assetId, data)
      const object = {}
      object[assetId] = data
      Vue.set(state, 'assetLogs', Object.assign({}, state.assetLogs, object))
    },
    setDeviceDefinitions (state, { data }) {
      console.log('setDeviceDefinitions', data)
      Vue.set(state, 'deviceDefinitions', data)
    },
    setDeviceTypes (state, { data }) {
      Vue.set(state, 'deviceTypes', data)
    },
    setErrors (state, { modelName, errors }) {
      Vue.set(state.itemErrors, modelName, errors)
    },
    handleAxiosResponse (state, { modelName, response }) {
      console.log('[store][handleAxiosResponse]')
      if (!response || !response.status) {
        // Network error
        console.log('[store][handleAxiosResponse] Network error')
        // Flag the error
        vuexSetError(state, 'networkError', true)
      } else if (response.status === 401) {
        console.log('[store][handleAxiosResponse] unauthorizedError', state.errors.unauthorizedError)
        // reload webpage when new version was deployed
        if (response.data.error === 'new-version') {
          Vue.set(state, 'newVersionError', true)
        }
        // Flag the error (user is logged-out by main-window component)
        vuexSetError(state, 'unauthorizedError', true)
      } else if (response.status >= 200 && response.status <= 299) {
        console.log('[store][handleAxiosResponse] ERR: 200 received in error handler')
        // 200 should never be here as this is only used for handling error responses
      } else if (response.status === 422) {
        console.log('[store][handleAxiosResponse] 422')
        // Setting validation errors
        if (modelName) {
          vuexSetErrors(state, modelName, response.data.errors)
        }
        // Setting generic error texts and triggering the generic error to display them
        let errors = Object.keys(response.data.errors)
        if (errors.length) {
          let messages = errors.map(key => response.data.errors[key])
          state.errors.errorTexts = [].concat.apply([], messages)
          vuexSetError(state, 'genericError', true)
        }
      } else if (response.status === 500) {
        console.log('[store][handleAxiosResponse] Server error', response)
        vuexSetError(state, 'serverError', true)
      } else {
        // Other error
        console.log('[store][handleAxiosResponse] Error')
        // Flag the error
        vuexSetError(state, 'genericError', true)
      }
    },
    showDestroyDialog (state, payload) {
      console.log('[store][showDestroyDialog], payload: ' + payload)
      Object.assign(state.destroyDialog, payload)
    },
    hideDestroyDialog (state) {
      state.destroyDialog = Object.assign({}, initDestroyDialog)
    },
    showUnsavedDialog (state, payload) {
      console.log('[store][showUnsavedDialog], payload: ' + payload)
      Object.assign(state.unsavedDialog, payload)
    },
    hideUnsavedDialog (state) {
      state.unsavedDialog = Object.assign({}, initUnsavedDialog)
    },
    setFiles (state, { id, data, size }) {
      let fileData = Object.assign({}, state.files[id])
      fileData[size.toString()] = (fileData[size.toString()] ? fileData[size.toString()] : data)
      Vue.set(state.files, id, fileData)
    },
    showSnackbar (state, { message, timeout = 5000 }) {
      state.snackbar = {
        message,
        timeout
      }
    },
    clearLogsCache ({}, assetId) {
      logsCache.clear(assetId)
    }
  },

  actions: {
    async getServerTime() {
      const { data } = await axios.get(`/get-server-time`)
      return Number(data)
    },
    async getItems ({ commit, state }, { model, customParams = {}, saveToStore = true }) {
      console.log('getItems, model.name: ' + model.name)

      let params = {
        sort_column: model.sort_col,
        sort_direction: model.sort_ord,
        sort: model.sort,
        limit: model.limit,
        offset: model.offset,
        ownerId: state.auth.currentOwnerId
      }

      params = Object.assign(params, customParams)

      try {
        const response = await axios.get(`/${model.name}`, {
          params: params
        })
        if (saveToStore) {
          commit('setItems', {
            modelName: model.name,
            modelTree: model.tree,
            items: response.data
          })
        }
        return response.data
      } catch (error) {
        commit('handleAxiosResponse', { modelName: '', response: error.response })
        return Promise.reject(error)
      }
    },

    getManyItems ({ commit, state }, { model, customParams = {}, toBeginning = false }) {
      // this is alternative of getItems used for infinity loading
      // offset and limit must be set in model
      console.log('getManyItems, model.name: ' + model.name)

      let params = {
        sort_column: model.sort_col,
        sort_direction: model.sort_ord,
        limit: model.limit,
        offset: model.offset
      }

      Object.assign(params, customParams)

      return axios.get(`/${model.name}`, {
        params: params
      })
        .then((response) => {
          commit('addManyItems', {
            modelName: model.name,
            items: response.data,
            toBeginning
          })

          return response
        }, (error) => {
          commit('handleAxiosResponse', {
            modelName: '',
            response: error.response
          })

          return error.response
        })
    },

    async getStaticItems ({ dispatch, state }, { model }) {
      // Check whether model is already loaded
      if (state.staticItems[model.name] && (model.tree && state.staticItems[model.name] instanceof TreeModel || !model.tree && state.staticItems[model.name] instanceof Array)) {
        console.log('getStaticItems, model.name: ' + model.name + ', already loaded')
        return Promise.resolve(state.staticItems[model.name])
      }

      const result = await dispatch('refreshStaticItems', { model })
      return Promise.resolve(result.data)
    },

    async refreshStaticItems({ commit }, { model }) {
      console.log('getStaticItems, model.name: ' + model.name + ', loading...')
      // Return a Promise to enable chaining with .then
      try {
        const response = await axios.get(`/${model.name}`)
        // OK
        commit('setStaticItems', { modelName: model.name, modelTree: model.tree, items: response.data })
        return Promise.resolve(response)
      } catch (error) {
        // error callback
        commit('handleAxiosResponse', { modelName: '', response: error.response })
        return Promise.reject(error.response)
      }
    },

    async getItem ({ commit, state }, { modelName, id, newItem }) {
      // TODO: Handle non-existing id requests
      console.log('getItem, modelName: ' + modelName + ', id:' + id)
      if (id > 0) {
        // show action (existing item)
        try {
          const response = await axios.get(`/${modelName}/${id}`)
          commit('setItem', { modelName, item: response.data })
          return Promise.resolve(JSON.parse(JSON.stringify(state.item[modelName])))
        } catch (error) {
          switch (error.response.status) {
            case 404: // hack for catching non-existing entity and routing user to different page
              commit('setItem', { modelName, item: { id: 0 } })
              break
            default:
              commit('handleAxiosResponse', { modelName: '', response: error.response })
              return error.response
          }
        }
      } else {
        // new action (new item)
        // using POST instead of GET as POST is much better for sending JSON as payload (Michal)
        // newItem must be in format item: {...} where item is singularized model name
        try {
          const response = await axios.post(`/${modelName}/new`, newItem)
          commit('setItem', { modelName, item: response.data })
        } catch (error) {
          commit('handleAxiosResponse', { modelName: '', response: error.response })
          return error.response
        }
      }
    },
    async createIssueForMultipleAlarms ({ dispatch }, item) {
      console.log('test')
      console.log(item)
      const asd = item
      try {
        const response = await axios.post(`/issues`, asd)
        return Promise.resolve(response)
      } catch (error) {
        commit('handleAxiosResponse', { modelName, response: error.response })
        return Promise.reject(error)
      }

    },
    async updateItem ({ commit, dispatch }, { modelName, item }) {
      console.log('updateItem, modelName: ' + modelName + ', id:' + item.id)
      if (item.id > 0) {
        // update action (existing item)
        console.log('update')
        try {
          await axios.put(`/${modelName}/${item.id}`, item)
          return await dispatch('getItem', { modelName, id: item.id })
        } catch (error) {
          commit('handleAxiosResponse', { modelName, response: error.response })
          return Promise.reject(error)
        }
      } else {
        // create action (new item)
        console.log('create')
        try {
          const response = await axios.post(`/${modelName}`, item)
          return Promise.resolve(response.data)
        } catch (error) {
          commit('handleAxiosResponse', { modelName, response: error.response })
          return Promise.reject(error)
        }
      }
    },
    async massUpdate ({ commit }, { modelName, propertyName, items }) {
      try {
        const values = items.map(x => x[propertyName]).filter(x => !!x)

        await axios.post('/mass-update', {
          entityIds: items.map(x => parseInt(x.id)),
          entityName: modelName,
          column: propertyName,
          ...(values.length ? { values } : {})
        }
        )

        return Promise.resolve()

      } catch (error) {
        commit('handleAxiosResponse', { modelName, response: error.response })

        return Promise.reject()
      }
    },
    async destroyItem ({ commit }, { model, id }) {
      console.log('archiveItem, model.name: ' + model.name + ', id:' + id)
      try {
        const response = await axios.delete(`/${model.name}/${id}`)
        return Promise.resolve(response)
      } catch (error) {
        commit('handleAxiosResponse', { modelName: model.name, response: error.response })
        return Promise.reject(error.response)
      }
    },
    async undoDestroyItem ({ commit }, { model, item, getItem = false }) {
      try {
        const archivedItem = {
          ...item,
          archivedAt: null
        }
        const { id } = archivedItem
        await axios.put(`/${model}/${id}`, archivedItem)
        if (getItem) {
          await dispatch('getItem', { modelName: model, id })
        }
        return Promise.resolve()
      } catch (error) {
        commit('handleAxiosResponse', { modelName, response: error.response })
        return Promise.reject(error)
      }
    },
    async massDestroy ({ dispatch }, { modelName, items }) {
      return dispatch('massUpdate', { modelName, propertyName: 'archived_at', items })
    },
    async authUser ({ commit }, { email, password, frontendVersion }) {
      console.log('[store][authUser]')
      try {
        const response = await axios.post('/auth-user', { email: email, password: password, frontendVersion })
        return response
      } catch (error) {
        commit('handleAxiosResponse', { modelName: '', response: error.response })
        return Promise.reject(error)
      }
    },
    solveIssue ({ commit }, { issue }) {
      console.log('solveIssue - issue id: ' + issue.id)
      return axios.post(`/issues/solve`, issue)
        .then((response) => {
          // OK
          return response
        }, (error) => {
          // error callback
          commit('handleAxiosResponse', { modelName: 'issues', response: error.response })
          return error.response
        })
    },
    updateOwnUser ({ commit }, { user }) {
      console.log('updateOwnuser')
      return axios.put(`/users/update-own`, user)
        .then((response) => {
          // OK
          return response
        }, (error) => {
          // error callback
          commit('handleAxiosResponse', { modelName: 'users', response: error.response })
          return error.response
        })
    },
    resetMyPassword ({ commit }, email) {
      console.log('resetMyPassword, email: ' + email)
      return axios.post(`/create-magic-link`, { email: email })
        .then((response) => {
          // OK
          return response
        }, (error) => {
          // error callback
          commit('handleAxiosResponse', { modelName: '', response: error.response })
          return error.response
        })
    },
    downloadReport ({ commit }, { name, format = 'json', dateFrom, dateTo, issueCategory, timespanFormat, assetTypeId }) {
      console.log('downloadReport, timespanFormat: ' + timespanFormat)
      return axios.get('/reports', {
        params: { name, format, dateFrom, dateTo, issueCategory, timespanFormat, assetTypeId }
      })
        .then((response) => {
          // OK
          commit('setReportItems', { items: response.data })
          return response
        }, (error) => {
          // error callback
          commit('handleAxiosResponse', { modelName: '', response: error.response })
          return error.response
        })
    },
    downloadReportExcel ({ commit }, { name, format = 'excel', dateFrom, dateTo, issueCategory }) {
      console.log('downloadReport')
      return axios.get('/reports',
        { params: { name, format, dateFrom, dateTo, issueCategory }, responseType: 'blob' })
        .then((response) => {
          // OK
          return response
        }, (error) => {
          // error callback
          commit('handleAxiosResponse', { modelName: '', response: error.response })
          return error.response
        })
    },
    async getAssetsInitValues ({ commit }, { visualId }) {
      console.log('[store][getAssetsInitValues]')
      try {
        await axios.post(`/get-assets-info`, { visualId })
      } catch (error) {
        console.error(error)
        commit('handleAxiosResponse', { modelName: '', response: error.response })
        return Promise.reject(error)
      }
    },
    createCommandToken ({ commit }, { commandPassword, fingerprint }) {
      return axios.post(`/create-command-token`, { commandPassword, fingerprint })
        .then((response) => {
          // OK
          return response
        }, (error) => {
          // error callback
          commit('handleAxiosResponse', { modelName: '', response: error.response })
          return error.response
        })
    },
    async loadComputerName ({ commit }, { hash }) {
      console.log('[store][loadComputerName]')
      try {
        const url = `/computer-name?hash=${hash}`
        const response = await axios.get(url)
        return Promise.resolve(response)
      } catch (error) {
        commit('handleAxiosResponse', { modelName: '', response: error.response })
        return Promise.reject(error)
      }
    },
    async saveComputerName ({ commit }, { name, hash }) {
      console.log('[store][saveComputerName]')
      try {
        const response = await axios.post(`/computer-name`, { name, hash })
        return Promise.resolve(response)
      } catch (error) {
        commit('handleAxiosResponse', { modelName: '', response: error.response })
        return Promise.reject(error)
      }
    },
    async callAssetMethod ({ commit }, { assetId, method, commandToken, attribute, params }) {
      console.log('[store][callAssetMethod]', 'assetId:', assetId, 'method:', method)
      try {
        // all OK
        const response = await axios.post(`/send-command`, { assetId, method, commandToken, attribute, params })
        return Promise.resolve(response)
      } catch (error) {
        const { response } = error
        if (response.status === 403 && (/unauthorized computer/i).test(response.data.errors.message)) {
          // destroy command token
          window.$cookies.remove('command')
          commit('showSnackbar', { message: i18n.t('invalid-command-token') })
        } else {
          // general error
          commit('handleAxiosResponse', { modelName: '', response })
        }
        return Promise.resolve(response)
      }
    },
    sendSafetyCommandRequest ({ commit }, { assetId, commandToken, userPassword, safetyToken }) {
      console.log('[store][sendSafetyCommandRequest]')

       return axios.post(`/commit-safety-request`, { assetId, commandToken, userPassword, safetyToken  })
        .then((response) => {
          // OK

          console.log(response.data)
          this.receivedSafetyToken = ''
        }, (error) => {
          // error callback
          commit('handleAxiosResponse', { modelName: '', response: error.response })
          console.log(error.response)
        })
    },
    askDestroy ({ commit }, { title, body }) {
      console.log('[store][askDestroy]')
      return new Promise((resolve, reject) => {
        commit('showDestroyDialog', {
          active: true,
          title,
          body,
          resolve,
          reject
        })
      })
    },
    askSave ({ commit }, { title, body }) {
      console.log('[store][askSave]')
      return new Promise((resolve, reject) => {
        commit('showUnsavedDialog', {
          active: true,
          title,
          body,
          resolve,
          reject
        })
      })
    },
    async updateVisualElements ({ commit }, { visual, elements }) {
      console.log('[store][updateVisualElements]')
      try {
        const response = await axios.put(`/visuals/${visual.id}/elements`, elements)
        this.dispatch('getItem', { modelName: 'visuals', id: visual.id })
        return response
      } catch (error) {
        commit('handleAxiosResponse', { modelName: 'visuals', response: error.response })
        return error.response
      }
    },
    getSiteConfig ({ commit }) {
      return axios.get(`/site`)
        .then(response => {
          const options = {
            center: { lat: response.data.centerLat, lng: response.data.centerLng },
            style: response.data.mapStyle
          }
          
          this.commit('setSite', response.data)
          this.commit('setMapOptions', options)
          
          return response
          
        }, error => {
          // error callback
          commit('handleAxiosResponse', { modelName: 'visuals', response: error.response })
          return error.response
        })
    },
    postponeOutdatedPipeTasks ({ commit }) {
      console.log('[store][postponeOutdatedPipeTasks]')
      return axios.post(`/pipe-tasks/postpone-outdated-to-today`)
        .then((response) => {
          // OK
          return response
        }, (error) => {
          // error callback
          commit('handleAxiosResponse', { modelName: '', response: error.response })
          return error.response
        })
    },
    async getLogs ({ commit, dispatch }, { assetId, timeFrom, timeTo }) {
      const formatDate = (time) => moment(time).utc().toISOString()
      const intervals = logsCache.calculateMissingIntervals({ assetId, timeFrom, timeTo })

      const results = await Promise.allSettled(
        intervals.map(({ timeFrom, timeTo }) => axios.get(`/assets/${assetId}/logs`, { params: { timeFrom: formatDate(timeFrom), timeTo: formatDate(timeTo) } }))
      )

      const logs = await Promise.all(
        results.map(result => result.status === 'rejected' ? dispatch('getItem', { modelName: 'assets', id: assetId }) : Promise.resolve(result.value.data)
      ))

      logs.forEach((data, index) => {
        const { timeFrom, timeTo } = intervals[index]
        const now = new Date().valueOf()

        if (!data.variables)
          data.variables = {}

        logsCache.saveData({ assetId, timeFrom, timeTo, now, data })
      })

      const data = logsCache.getData({ assetId, timeFrom, timeTo })
      commit('setLogs', { assetId, data })
    },
    async getLogsExport ({ commit }, { assetId, timeFrom, timeTo }) {
      console.log('[store][getLogsExport]')
      try {
        const formatDate = (time) => moment(time).utc().toISOString()
        const { data } = await axios.get(`/assets/${assetId}/logs`, { params: {
            timeFrom: formatDate(timeFrom),
            timeTo: formatDate(timeTo)
          }})
        return Promise.resolve(data)
      } catch (error) {
        // commit('handleAxiosResponse', { modelName: '', response: error.response })
        return Promise.reject(error)
      }
    },
    getDeviceStatisticsReport ({ commit }, { assetId, variables, queryType, limit, timeFrom, timeTo }) {
      const formatDate = (time) => moment(time).utc().toISOString()
      return axios.get('/reports/device-statistics',
        { params: { assetId, variables, queryType, limit, timeFrom: formatDate(timeFrom), timeTo: formatDate(timeTo) } })
        .then(response => {
          commit('setDeviceStatisticsReportData', { assetId, data: response.data })
          return response
        }, error => {
          commit('handleAxiosResponse', { modelName: '', response: error.response })
          return error.response
        })
    },
    getDeviceDefinitions ({ commit }) {
      console.log('[store][getDeviceDefinitions]')
      return axios.get(`/device-types`, { params: { mode: 'configs' } })
        .then(response => {
          // OK
          const variablesInArray = Object.keys(response.data).reduce((result, typeName) => {
            result[typeName] = Object.keys(response.data[typeName]['variables'])
            return result
          }, {})
          commit('setDeviceDefinitions', { data: variablesInArray })
          return response
        }, error => {
          // error callback
          commit('handleAxiosResponse', { modelName: '', response: error.response })
          return error.response
        })
    },
    async getDeviceTypes ({ commit }) {
      console.log('[store][getDeviceTypes]')
      try {
        const response = await axios.get(`/device-types`)
        commit('setDeviceTypes', { data: response.data })
      } catch (error) {
        console.error(error)
        return Promise.reject(error)
      }
    },
    importCSV ({ commit }, { csvFile }) {
      // Used for batch-importing data from CSV into the database
      console.log('[store][importCSV]')
      return axios.post(`/import-csv`, csvFile, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
        .then(response => {
          // OK
          return response
        }, error => {
          // error callback
          commit('handleAxiosResponse', { modelName: '', response: error.response })
          return error.response
        })
    },
    async checkFrontendVersion ({ commit }, { frontendVersion }) {
      try {
        await axios.get(`/check-frontend-version`, { params: { frontendVersion } })
      } catch (error) {
        window.localStorage.setItem('retObserverFrontendVersion', error.response.data.newVersion)
        commit('handleAxiosResponse', { modelName: '', response: error.response })
        return Promise.reject(error)
      }
    },
    async getFile ({ commit, state }, { fileId, requiredSize = '_orig' }) {
      if (state.files[fileId] && state.files[fileId][requiredSize]) {
        return  // already loaded
      }
      try {
        const response = await axios({
          method: 'get',
          url: '/files/' + fileId + '/download' + (requiredSize ? ('?size=' + requiredSize) : ''),
          responseType: 'arraybuffer'
        })
        let dataIntArray = new Uint8Array(response.data)
        let dataCharArray = []
        dataIntArray.forEach((element) => {
          dataCharArray.push(String.fromCharCode(element))
        })
        let appliedString = dataCharArray.join('')
        commit('setFiles', {
          id: fileId,
          data: 'data:' + response.headers['content-type'] + ';base64,' + window.btoa(appliedString),
          size: requiredSize
        })
      } catch (error) {
        console.log(error)
      }
    },
    async uploadFile ({ commit }, { modelName, modelItemId, formData, resize = false }) {
      if (!modelName || !modelItemId) await Promise.reject(Error('modelName and/or modelItemId cannot be null!'))
      try {
        let url = '/files?modelName=' + modelName + '&itemId=' + modelItemId
        if (resize) {
          url += '&resize=true'
        }
        return await axios.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      } catch (error) {
        commit('handleAxiosResponse', { modelName, response: error.response })
        return Promise.reject(error)
      }
    },
    async runConditionMonitoringManually ({ commit }) {
      try {
        return await axios.get(`/run-monitoring-manually`)
      } catch (error) {
        commit('handleAxiosResponse', { modelName: '', response: error.response })
        return Promise.reject(error)
      }
    },
    async calculateMonitoringReferenceValues ({ commit }, { assetId, data }) {
      try {
        return await axios.post(`/assets/${assetId}/calculate-monitoring-references`, data)
      } catch (error) {
        commit('handleAxiosResponse', { modelName: '', response: error.response })
        return Promise.reject(error)
      }
    }
  }
})

// Modules registration
store.registerModule('checklist', checklistStore)
store.registerModule('lazyItems', lazyItemsStore)
store.registerModule('map', mapStore)
store.registerModule('filters', filtersStore)

export default store
